/**
 * W76.4 — Bitcoin Halving-Cycle Position-Tracker.
 *
 * BTC-Halving alle ~4 Jahre (210 000 Blocks ≈ 1458 Tage). Statistik der
 * letzten 3 Cycles zeigt: 12-18 Monate nach Halving = Bull-Peak, 18-30
 * Monate = Bear-Bottom, 30-46 Monate = Akkumulation, dann nächstes Halving.
 *
 * Diese Funktion liefert die aktuelle Cycle-Position als Hero-Metric +
 * Phase-Label fuer Krypto-Trader.
 *
 * Audit Agent F HIGH: war als Top-1-Empfehlung gelistet — XS-Aufwand,
 * kein API noetig, hoher User-Wert.
 */

export const LAST_HALVING_DATE = new Date('2024-04-19T00:00:00Z');
const CYCLE_DAYS = 1458; // ~210 000 Blocks × 10 Min

export type HalvingCyclePhase =
  | 'early_bull' // 0-12 Mon nach Halving
  | 'late_bull' // 12-18 Mon
  | 'top_distribution' // 18-24 Mon
  | 'bear' // 24-30 Mon
  | 'bottom_accumulation' // 30-42 Mon
  | 'pre_halving'; // 42-48 Mon

export interface HalvingCycleStatus {
  /** Tag seit letztem Halving (0..1458). */
  days_since_halving: number;
  /** Tage bis zum naechsten erwarteten Halving (~1458 - days_since). */
  days_until_next_halving: number;
  /** Aktuelle Phase des 4-Jahres-Cycles. */
  phase: HalvingCyclePhase;
  /** Klartext-Beschreibung (DE). */
  label: string;
  /** Cycle-Position als 0..1 (fuer Progress-Bar). */
  progress_pct: number;
}

export function computeHalvingCycle(today: Date = new Date()): HalvingCycleStatus {
  const daysSince = Math.floor(
    (today.getTime() - LAST_HALVING_DATE.getTime()) / 86400000
  );
  const daysUntil = Math.max(0, CYCLE_DAYS - daysSince);
  const progress = Math.min(1, Math.max(0, daysSince / CYCLE_DAYS));

  let phase: HalvingCyclePhase;
  let label: string;

  if (daysSince < 365) {
    phase = 'early_bull';
    label = `Early-Bull · Tag ${daysSince} von ~1458 · Akkumulation der Smart-Money`;
  } else if (daysSince < 540) {
    phase = 'late_bull';
    label = `Late-Bull · Tag ${daysSince} · Retail-Inflow + Top-Anzeichen erwartet`;
  } else if (daysSince < 720) {
    phase = 'top_distribution';
    label = `Top-Distribution · Tag ${daysSince} · Verteilung an Retail, Risiko hoch`;
  } else if (daysSince < 900) {
    phase = 'bear';
    label = `Bear-Phase · Tag ${daysSince} · Crash-Risiko 50-80%, Stops eng halten`;
  } else if (daysSince < 1260) {
    phase = 'bottom_accumulation';
    label = `Bottom-Accumulation · Tag ${daysSince} · Smart-Money beginnt zu kaufen`;
  } else {
    phase = 'pre_halving';
    label = `Pre-Halving · Tag ${daysSince} · Naechstes Halving in ${daysUntil}T`;
  }

  return {
    days_since_halving: daysSince,
    days_until_next_halving: daysUntil,
    phase,
    label,
    progress_pct: progress,
  };
}
